import { defineStore } from "pinia";
import axiosInstance from "@/api/axios"; // Axios 인스턴스 가져오기
import getVapidPublicKey from "@/utils/webpush";

// ✅ 그룹 기반 권한 헬퍼 — superuser는 모든 역할 통과.
//    (메뉴 노출 menuStore.canSee, Django superuser 철학과 일관.
//     이전에는 getter마다 제각각이라 superuser가 견적 403, 샘플 버튼 숨김 등 비일관 발생)
const isSu = (state) => state.profile?.is_superuser === true;
const inGroup = (state, name) =>
  isSu(state) || (state.profile?.groups || []).includes(name);
const inAnyGroup = (state, names) =>
  isSu(state) || names.some((g) => (state.profile?.groups || []).includes(g));

export const useUserStore = defineStore("user", {
  state: () => ({
    profile: {
      username: '', // 기본값 설정
      first_name:'',
      last_name:'',
      department: '',
      groups: [],
      email: '',
    },
    isAuthenticated: false, // 로그인 상태
    }
  ),
  getters: {
    // ✅ 로그인한 사용자의 부서 ID 가져오기 (없으면 null)
    department_id: (state) => (state.profile?.department ? state.profile.department.id : null),

    // 그룹 배열 안전하게 가져오기 (null/undefined 방지)
    groups: (state) => state.profile?.groups || [],

    // 특정 그룹 여부 체크 (superuser는 전부 통과)
    isSupplierManager: (state) => inGroup(state, "매입처관리자"),

    isLeaveRequestor: (state) => inGroup(state, "휴가신청자"),
    isLeaveApprover: (state) => inGroup(state, "휴가승인자"),
    // 휴가 관리자(=인사담당자). 백엔드에 '휴가관리자' 그룹은 없고 '인사담당자'가 휴가 운영 권한을 겸함.
    isLeaveManager: (state) => inGroup(state, "인사담당자"),

    isExpenseRequestor: (state) => inGroup(state, "지출결의서작성자"),
    isExpenseReceiver: (state) => inGroup(state, "지출결의서접수자"),
    isExpenseExecutor: (state) => inGroup(state, "지출결의서집행자"),

    // 일별 지출통계(피봇/드릴다운) 조회 권한
    canViewExpenseReport: (state) =>
      inAnyGroup(state, ["지출통계조회자", "지출결의서접수자", "지출결의서집행자"]),

    // ✅ 회사 일정 관리자 여부 (모든 부서 일정 조회 가능)
    isCompanyScheduler: (state) => inGroup(state, "회사일정관리자"),

    // ✅ 부서 일정 관리자 여부 (자신의 부서 일정만 조회 가능)
    isDepartmentScheduler: (state) => inGroup(state, "부서일정관리자"),

    isScheduler: (state) => inAnyGroup(state, ["부서일정관리자", "회사일정관리자"]),

    isNoticeManager: (state) => inGroup(state, "공지사항관리자"),

    // 관리자 권한 (superuser는 모든 메뉴 접근 가능)
    isSuperuser: (state) => state.profile?.is_superuser === true,
    isAdmin: (state) => state.profile?.is_superuser === true || state.profile?.is_staff === true,

    // 원가 관리 권한
    isCostViewer: (state) => inGroup(state, "원가조회자"),
    isCostManager: (state) => inGroup(state, "원가관리자"),
    hasCostAccess: (state) => inAnyGroup(state, ["원가조회자", "원가관리자"]),

    // 견적 관리 권한
    isQuotationViewer: (state) => inGroup(state, "견적조회자"),
    isQuotationManager: (state) => inGroup(state, "견적관리자"),
    hasQuotationAccess: (state) => inAnyGroup(state, ["견적조회자", "견적관리자"]),

    // 메인오더/생산 관리 권한
    isMainRequestManager: (state) => inGroup(state, "메인요청관리자"),
    isProductionManager: (state) => inGroup(state, "생산관리자"),
    isMaterialOrderManager: (state) => inGroup(state, "자재발주관리자"),

    // 무역/LC 관리 권한
    isLCManager: (state) => inGroup(state, "LC관리자"),

    // 스케줄 알림 수신자
    isScheduleAlertReceiver: (state) => inGroup(state, "스케줄알림수신자"),

    // 샘플관리 권한
    isSampleManager: (state) => inGroup(state, "샘플관리자"),
    isSampleApprover: (state) => inGroup(state, "샘플승인자"),
    hasSampleAccess: (state) => inAnyGroup(state, ["샘플관리자", "샘플승인자"]),

    // 그룹 체크 헬퍼 — 권한 판정용 (superuser 통과)
    hasGroup: (state) => (groupName) => inGroup(state, groupName),
    hasAnyGroup: (state) => (groupNames) => inAnyGroup(state, groupNames),
    // 부조금 관리
    isCondolenceManager: (state) => inGroup(state, "부조금관리자"),

  },

  actions: {

    
    async fetchUserProfile() {
      try {
        //console.log("userStore: featchUserProfile==================== ")
        const token = localStorage.getItem("access_token");

        if (!token) throw new Error("No token found");
        //console.log("userStore: token ==================== " ,token)

        const response = await axiosInstance.get("user/me/", {
          headers: { Authorization: `Bearer ${token}` },
        });

        //console.log("userStore response=",response.data)
        //this.profile = response.data;
        
        if (response.data ) {
            this.profile= response.data; // 배열의 첫 번째 항목 처리
            this.isAuthenticated = true; // 인증 상태 설정
        } else {
          throw new Error("Invalid user profile data");
        }
        
      } catch (error) {
        console.error("Failed to fetch user profile:", error);
        this.isAuthenticated = false;
        // refresh token 갱신은 axios 인터셉터에서 자동 처리됨
        // 여기 도달 시 refresh도 실패한 상태이므로 로그아웃
        this.logout();
      }
    },
     /**
     * 🔹 푸시 알림 구독
     * 로그인한 사용자만 구독하며, 서버에 구독 정보 전송
     */
     async subscribeToPush() {
      if (!this.profile || !this.isAuthenticated) return; // 로그인한 사용자만 구독 가능

      if ("serviceWorker" in navigator && "PushManager" in window) {
        try {

          const reg = await navigator.serviceWorker.register("/sw.js");

          const vapidPublicKey = await getVapidPublicKey();

          console.log("🔍 변환된 VAPID_PUBLIC_KEY 확인:", vapidPublicKey);


          const applicationServerKey = await  urlBase64ToUint8Array(vapidPublicKey);

          console.log("🔍 변환된 applicationServerKey 확인:", applicationServerKey);

          const subscription = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey:applicationServerKey,
          });

          console.log("✅ 푸시 구독 정보:", subscription);

          // 서버에 구독 정보 전송
          await axiosInstance.post("/webpush/subscribe/",{
                  subscription:subscription.toJSON(),
                  browser:navigator.userAgent
                },{
                  headers: { "Content-Type": "application/json" }
                })
              .then(() => console.log("✅ 구독 정보 백엔드 저장 완료"))
              .catch(err => console.error("❌ 백엔드 저장 실패:", err));
          
        } catch (error) {
          console.error("❌ 푸시 구독 오류:", error);
        }
      } else {
        console.warn("❌ 브라우저가 푸시 알림을 지원하지 않습니다.");
      }
    },

     /**
     * 🔹 푸시 알림 구독 해제
     * 로그아웃 시 기존 푸시 구독을 취소
     */
     async unsubscribeFromPush() {
      if ("serviceWorker" in navigator && "PushManager" in window) {
        try {
          const reg = await navigator.serviceWorker.getRegistration();
          if (reg) {
            const subscription = await reg.pushManager.getSubscription();
            if (subscription) {
              await subscription.unsubscribe();
              console.log("🔴 푸시 알림 구독 취소 완료!");
            }
          }
        } catch (error) {
          console.error("❌ 푸시 구독 취소 오류:", error);
        }
      }
    },

    async logout() {
      try{
        const refresh_token = localStorage.getItem("refresh_token");
        if (refresh_token) {
            await axiosInstance.post("logout/", { refresh_token });
        }

      }catch(error){
        console.error("Logout failed", error);
      }finally{
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");
        this.profile = null;
        this.isAuthenticated = false;}
    },
  },
});


// Base64 to Uint8Array 변환 함수
export function urlBase64ToUint8Array(base64String) {
    
  // ✅ Base64 URL Safe 변환 (패딩 추가)
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);

  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  // ✅ Base64 디코딩 후 Uint8Array 변환
  const rawData = atob(base64);

  const uint8Array = new Uint8Array([...rawData].map((char) => char.charCodeAt(0)));

  return uint8Array;
}